import { useCallback, useState } from 'react';

/**
 * Which of the two Project Plan views is showing (IB-793).
 *
 * `hierarchy` is the plain Category → Sub-category → Activity tree. `source` keeps that
 * same tree but groups the top-level categories under a band per source (Master, a
 * department/cost centre, a venue, or the procurement plan), because at the aggregate
 * programme level the same category names repeat across sources and are otherwise
 * indistinguishable.
 *
 * The toggle is offered on the central (master) plan only: a department, cost centre or
 * venue plan has exactly one source, so grouping by it would draw a single band around
 * everything. Those scopes are pinned to `hierarchy` and never read or write the stored
 * preference.
 */
export const VIEW_HIERARCHY = 'hierarchy';
export const VIEW_SOURCE = 'source';

const STORAGE_KEY = 'project-plan-view-v1';

const isKnownView = (value) => value === VIEW_HIERARCHY || value === VIEW_SOURCE;

/** By source is the default at programme level — that is the whole point of the view. */
const load = () => {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);

        return isKnownView(stored) ? stored : VIEW_SOURCE;
    } catch (e) {
        return VIEW_SOURCE;
    }
};

export default function useProjectPlanView(isMaster) {
    const [storedView, setStoredView] = useState(load);

    const setView = useCallback((next) => {
        if (!isKnownView(next)) {
            return;
        }

        setStoredView(next);

        try {
            window.localStorage.setItem(STORAGE_KEY, next);
        } catch (e) {
            // Ignore quota / privacy-mode failures — the choice still holds for the session.
        }
    }, []);

    const view = isMaster ? storedView : VIEW_HIERARCHY;

    return { view, setView, bySource: view === VIEW_SOURCE, canToggle: isMaster };
}
